<template>
  <Teleport to="#teleports">
    <div
      v-if="visible"
      v-bind="$attrs"
      ref="vfmRootEl"
      class="vfm vfm--fixed vfm--inset"
      :class="{
        'vfm--center': !scrollable
      }"
      :style="{ zIndex }"
      role="dialog"
      aria-modal="true"
      @keydown.esc="onEsc"
      @mouseup.self="onMouseupClose"
    >
      <Transition
        v-if="!hideOverlay"
        name="vfm-fade"
        appear
        v-on="overlayListeners"
      >
        <div
          v-if="overlayVisible"
          class="vfm--overlay vfm--absolute vfm--inset vfm--prevent-none"
          :class="overlayClass"
          :style="overlayStyle"
          aria-hidden="true"
        />
      </Transition>

      <Transition
        :name="contentTransition"
        appear
        v-on="contentListeners"
      >
        <div
          v-if="contentVisible"
          ref="vfmContentEl"
          class="vfm__content vfm--outline-none"
          :class="[
          scrollable ? 'vfm--inset vfm--absolute' : contentClass,
            {
              'vfm--body': !scrollable,
              'vfm__content--full': contentFullScreen,
              'modal--full-mobile': mobileFullScreen
            }
          ]"
          :style="!scrollable ? contentStyle : undefined"
          tabindex="0"
        >
          <template v-if="scrollable">
            <div
              class="vfm__content--scrollable vfm--inset"
              @mouseup.self="onMouseupClose"
              @scroll="emit('scroll-content', $event as UIEvent)"
            >
              <div
                class="vfm__content--scrollable-body"
                :class="{
                  'modal--full-mobile': mobileFullScreen,
                  'vfm__content--full': contentFullScreen
                }"
                @mouseup.self="onMouseupClose"
              >
                <div
                  class="vfm--body"
                  :style="contentStyle"
                  :class="contentClass"
                >
                  <slot
                    :close="_close"
                    :params="payloadParams"
                  />
                </div>
              </div>
            </div>
          </template>

          <slot
            v-else
            :close="_close"
            :params="payloadParams"
          />
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { ModalId, ModalExposed, VfmTransition, StyleValue } from '@/types/modal'
import { useLockScroll } from '~/composables/modals/useBodyScrollLock'
import { useModelValue } from '~/composables/modals/useModelValue'
import { useTransition } from '~/composables/modals/useTransition'
import { getModalExposed } from '~/plugins/modal'

export interface VueFinalModalEmits {
  (e: 'update:modelValue', modelValue: boolean): void
  (e: 'before-open', event: { stop: () => void, params?: object }): void
  (e: 'opened' | 'closed' | 'click-outside'): void
  (e: 'before-close', event: { stop: () => void }): void
  (e: 'scroll-content', value: UIEvent): void
}

type Props = {
  modalId: ModalId
  value?: boolean
  modelValue?: boolean
  hideOverlay?: boolean
  overlayBehavior?: 'auto' | 'persist'
  contentTransition?: VfmTransition
  overlayClass?: string | object | []
  contentClass?: string | object | []
  overlayStyle?: StyleValue
  contentStyle?: StyleValue
  clickToClose?: boolean
  escToClose?: boolean
  lockScroll?: boolean
  reserveScrollBarGap?: boolean
  scrollable?: boolean
  contentFullScreen?: boolean
  mobileFullScreen?: boolean
}

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  overlayBehavior: 'auto',
  contentTransition: 'vfm-fade',
  overlayClass: undefined,
  contentClass: undefined,
  overlayStyle: undefined,
  contentStyle: undefined,
  clickToClose: true,
  escToClose: true,
  lockScroll: true,
  reserveScrollBarGap: true
})
const emit = defineEmits<VueFinalModalEmits>()

const instance = getCurrentInstance()
const payloadParams = shallowRef<object | undefined>()

const { $vfm } = useNuxtApp()
const { modals, openedModals, openedModalOverlays } = $vfm

const vfmRootEl = useTemplateRef<HTMLDivElement>('vfmRootEl')
const vfmContentEl = useTemplateRef<HTMLDivElement>('vfmContentEl')

const zIndex = shallowRef<undefined | number>()

const refreshZIndex = (index: number) => {
  zIndex.value = 1000 + Math.max(0, index)
}
const resetZIndex = () => {
  zIndex.value = undefined
}

const { modelValueLocal, checkDisabledClose } = useModelValue(props, emit, { open, close })
const { enableBodyScroll, disableBodyScroll } = useLockScroll(props, {
  lockScrollEl: vfmRootEl,
  modelValueLocal
})

let resolveToggle: (res: string) => void = noop

const {
  visible,

  contentVisible,
  contentListeners,
  overlayVisible,
  overlayListeners,

  enterTransition,
  leaveTransition
} = useTransition(props, {
  modelValueLocal,
  onEntering() {
    nextTick(() => {
      vfmContentEl.value?.focus()
      disableBodyScroll()
    })
  },
  onEnter() {
    emit('opened')
    resolveToggle('opened')
  },
  onLeave() {
    arrayRemoveItem(openedModals, instance)
    resetZIndex()
    enableBodyScroll()
    emit('closed')
    resolveToggle('closed')
  }
})

function onEsc() {
  if (visible.value && props.escToClose) {
    modelValueLocal.value = false
  }
}

function onMouseupClose(): void {

  if (checkDisabledClose()) {
    return
  }
  if (props.clickToClose) {
    modelValueLocal.value = false
  } else {
    vfmContentEl.value?.focus()
    emit('click-outside')
  }
}

const index = computed(() => {
  if (!visible.value) {
    return -1
  }
  return instance ? openedModals.indexOf(instance) : -1
})

watch(index, () => {
  if (visible.value) {
    refreshZIndex(index.value)
  }
})

onMounted(() => {
  arrayMoveItemToLast(modals, instance)
})

if (props.modelValue) {
  modelValueLocal.value = true
}

function open(): boolean {
  let shouldStop = false

  emit('before-open', {
    stop: () => {
      shouldStop = true
    },
    params: payloadParams.value
  })

  if (shouldStop) {
    return false
  }
  arrayMoveItemToLast(openedModals, instance)
  arrayMoveItemToLast(openedModalOverlays, instance)
  openLastOverlay()
  enterTransition()
  return true
}

function close(): boolean {
  let shouldStop = false

  emit('before-close', {
    stop: () => {
      shouldStop = true
    }
  })
  if (shouldStop) {
    return false
  }

  arrayRemoveItem(openedModalOverlays, instance)
  openLastOverlay()
  leaveTransition()
  return true
}

/** Close function for scoped slot */
function _close() {
  modelValueLocal.value = false
}

onBeforeUnmount(() => {
  enableBodyScroll()
  arrayRemoveItem(modals, instance)
  arrayRemoveItem(openedModals, instance)
  // blur()
  openLastOverlay()
})

async function openLastOverlay() {
  await nextTick()
  // Found the modals which has overlay and has `auto` overlayBehavior
  const openedModalsOverlaysAuto = openedModalOverlays.filter((modal) => {
    const modalExposed = getModalExposed(modal)
    const {
      overlayBehavior,
      hideOverlay
    } = unref(modalExposed) || {}

    return unref(overlayBehavior) === 'auto' && !unref(hideOverlay)
  })
  // Only keep the last overlay open
  openedModalsOverlaysAuto.forEach((modal, index) => {
    const modalExposed = unref(getModalExposed(modal))

    if (modalExposed) {
      modalExposed.overlayVisible.value = index === openedModalsOverlaysAuto.length - 1
    }
  })
}

// const modalId = useToRef(props, 'modalId')
// const hideOverlay = useToRef(props, 'hideOverlay')
// const overlayBehavior = useToRef(props, 'overlayBehavior')

const modalExposed = computed<ModalExposed>(() => ({
  modalId: props.modalId,
  hideOverlay: props.hideOverlay,
  overlayBehavior: props.overlayBehavior,
  overlayVisible,
  toggle(show?: boolean, params?: object): Promise<string> {
    return new Promise((resolve) => {
      resolveToggle = once((res: string) => resolve(res))
      payloadParams.value = params ? { ...params } : undefined

      const newValue = typeof show === 'boolean'
        ? show
        : !modelValueLocal.value

      if (newValue !== modelValueLocal.value) {
        modelValueLocal.value = newValue
      }
    })
  }
}))

defineExpose({
  modalExposed
})
</script>

<style lang="scss">
.vfm--prevent-none {
  pointer-events: none;
}
.vfm--prevent-auto {
  pointer-events: auto;
}
.vfm--outline-none:focus {
  outline: none;
}

.vfm {
  $root: &;

  &--fixed {
    position: fixed;
    overscroll-behavior: none;
    z-index: 1000;
  }

  &--absolute {
    position: absolute;
  }

  &--center {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &--inset {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &--overlay {
    z-index: -1;
    background-color: rgba(0, 0, 0, 0.5);
  }

  &--body {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 20px 60px -2px #1b213a66;
    width: 100%;
    min-width: 320px;
    //width: auto;
    max-width: max-content;
    margin-left: auto;
    margin-right: auto;
    position: relative;
  }

  &__content {
    flex-shrink: 0;
    margin: 0 auto;
  }

  &__content--scrollable {
    position: absolute;
    overflow: auto;
    height: 100%;
    scrollbar-gutter: stable;
  }

  &__content--scrollable-body {
    padding-top: 2rem;
    padding-bottom: 2rem;
    touch-action: manipulation;
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100%;

    @media only screen and (min-width: 740px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }

  &__content--full {
    padding-top: 0;
    padding-bottom: 0;

    .vfm--body,
    &.vfm--body {
      max-width: initial;
      width: 100%;
      height: 100%;
    }
  }

  @media only screen and (min-width: 740px) and (max-width: 1024px) {
    .modal--full-tablet {
      padding-top: 0;
      padding-bottom: 0;

      .vfm--body,
      &.vfm--body {
        max-width: initial;
        width: 100%;
        height: 100%;
      }
    }
  }

  @media only screen and (max-width: 740px) {
    .modal--full-mobile {
      padding-top: 0;
      padding-bottom: 0;

      .vfm--body,
      &.vfm--body {
        max-width: initial;
        width: 100%;
        height: 100%;
      }
    }
  }
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fade-out {
  from { opacity: 1 }
  to { opacity: 0 }
}

.vfm-fade-enter-active {
  animation: fade-in .3s ease;
}
.vfm-fade-leave-active {
  animation: fade-out .3s ease;
}
.vfm-bounce-back {
  transition-property: transform;
  transition-duration: .3s;
}

.vfm-slide-up-enter-active,
.vfm-slide-up-leave-active,
.vfm-slide-down-enter-active,
.vfm-slide-down-leave-active {
  transition: transform .3s ease;
}
.vfm-slide-down-enter-from,
.vfm-slide-down-leave-to {
  transform: translateY(100vh) !important;
}
.vfm-slide-up-enter-from,
.vfm-slide-up-leave-to {
  transform: translateY(-100vh) !important;
}

.vfm-slide-right-enter-active,
.vfm-slide-right-leave-active,
.vfm-slide-left-enter-active,
.vfm-slide-left-leave-active {
  transition: transform .3s ease;
}
.vfm-slide-right-enter-from,
.vfm-slide-right-leave-to {
  transform: translateX(100vw) !important;
}
.vfm-slide-left-enter-from,
.vfm-slide-left-leave-to {
  transform: translateX(-100vw) !important;
}

.vfm-swipe-banner-back,
.vfm-swipe-banner-forward {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 27px;
  z-index: 10;
}
.vfm-swipe-banner-back {
  left: 0;
}
.vfm-swipe-banner-forward {
  right: 0;
}
</style>
